import { createApiClient, type Schemas } from "@bl/api-client";
import type { MovementKind, ResetPart } from "@bl/domain";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { today } from "./format";
import type { NoteStore } from "./notify";

export const api = createApiClient("");

export type Item = Schemas["ItemSummary"];
export type ItemDetail = Schemas["ItemDetail"];
export type Lot = Schemas["Lot"];
export type Movement = Schemas["Movement"];
export type Location = Schemas["Location"];
export type LocationKind = Schemas["LocationKind"];
export type Supplier = Schemas["Supplier"];
export type CatalogProduct = Schemas["CatalogProduct"];
export type CatalogSupplier = Schemas["CatalogSupplier"];
export type Spec = Schemas["Spec"];
export type MovementInput = Schemas["MovementInput"];
export type NewItem = Schemas["NewItem"];
export type ItemPatch = Schemas["ItemPatch"];
export type LotPatch = Schemas["LotPatch"];
export type Category = Schemas["Category"];
export type Invoice = Schemas["Invoice"];
export type InvoiceSummary = Schemas["InvoiceSummary"];
export type InvoiceLine = Schemas["InvoiceLine"];
export type InvoiceLot = Schemas["InvoiceLot"];
export type InvoicePatch = Schemas["InvoicePatch"];
export type AllocationMethod = Schemas["AllocationMethod"];
export type RecipeSummary = Schemas["RecipeSummary"];
export type RecipeDetail = Schemas["RecipeDetail"];
export type RecipeLine = Schemas["RecipeLine"];
export type RecipeInput = Schemas["RecipeInput"];
export type RecipeDraft = Schemas["RecipeDraft"];
export type BrewSystem = Schemas["BrewSystem"];
export type BrewSystemInput = Schemas["BrewSystemInput"];
export type StockImport = Schemas["StockImport"];
export type StockImportSummary = Schemas["StockImportSummary"];
export type StockImportCommit = Schemas["StockImportCommit"];
export type RecipeDraftSummary = Schemas["RecipeDraftSummary"];
export type BrewSummary = Schemas["BrewSummary"];
export type BrewSeries = Schemas["BrewSeries"];
export type BrewSeriesPatch = Schemas["BrewSeriesPatch"];
export type RepeatRule = Schemas["Repeat"];
export type BrewDetail = Schemas["BrewDetail"];
export type BrewPatch = Schemas["BrewPatch"];
export type HarvestInput = Schemas["HarvestInput"];
export type Tank = Schemas["Tank"];
export type TankInput = Schemas["TankInput"];
export type TankPatch = Schemas["TankPatch"];
export type TankStay = Schemas["TankStay"];
export type TankStayPatch = Schemas["TankStayPatch"];
export type TankUnit = Schemas["TankUnit"];
export type UnbookedBrew = Schemas["UnbookedBrew"];
export type CellarSettings = Schemas["CellarSettings"];
export type ShortfallItem = Schemas["ShortfallItem"];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Turns an openapi-fetch result into data or a thrown ApiError carrying the server's message. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined && result.response.ok) return result.data;
  const body = result.error as { error?: { code?: string; message?: string } } | undefined;
  throw new ApiError(
    result.response.status,
    body?.error?.code ?? "error",
    body?.error?.message ?? `Request failed (${result.response.status})`,
  );
}

export type SessionState = Schemas["SessionState"];

export const keys = {
  session: ["session"] as const,
  items: ["items"] as const,
  item: (id: string) => ["items", id] as const,
  locations: ["locations"] as const,
  suppliers: ["suppliers"] as const,
  catalogSuppliers: ["catalog-suppliers"] as const,
  movements: (filter: object) => ["movements", filter] as const,
  movementLog: (kind: MovementKind | null) => ["movements", "log", kind] as const,
  catalog: (q: string, category: string | null) => ["catalog", q, category] as const,
  borrowing: ["borrowing"] as const,
  invoices: ["invoices"] as const,
  invoice: (id: string) => ["invoices", id] as const,
  recipes: ["recipes"] as const,
  recipe: (id: string) => ["recipes", id] as const,
  recipeDrafts: ["recipe-drafts"] as const,
  brewSystems: ["brew-systems"] as const,
  tanks: ["tanks"] as const,
  tankStays: (from: string, to: string) => ["tank-stays", from, to] as const,
  cellarSettings: ["cellar-settings"] as const,
  stockImports: ["stock-imports"] as const,
  stockImport: (id: string) => ["stock-imports", id] as const,
  recipeDraft: (id: string) => ["recipe-drafts", id] as const,
  brews: ["brews"] as const,
  brew: (id: string) => ["brews", id] as const,
  brewSeries: (id: string) => ["brew-series", id] as const,
  shortfall: (through: string) => ["shortfall", through] as const,
};

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: async () => unwrap(await api.GET("/api/v1/session")),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export async function signIn(password: string): Promise<SessionState> {
  return unwrap(await api.POST("/api/v1/session", { body: { password } }));
}

export async function signOut(): Promise<SessionState> {
  return unwrap(await api.DELETE("/api/v1/session"));
}

export function useItems() {
  return useQuery({
    queryKey: keys.items,
    queryFn: async () => unwrap(await api.GET("/api/v1/items")).items,
  });
}

/** Every item, archived included, for inventory management in Settings. */
export function useAllItems() {
  return useQuery({
    queryKey: [...keys.items, "all"] as const,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/items", { params: { query: { includeArchived: "true" } } }))
        .items,
  });
}

export function useItem(id: string) {
  return useQuery({
    queryKey: keys.item(id),
    queryFn: async () => unwrap(await api.GET("/api/v1/items/{id}", { params: { path: { id } } })),
  });
}

/** A list that waits to load until it is wanted: the header's search loads the brewery's when typed in. */
export interface WhenWanted {
  enabled?: boolean;
}

export function useLocations({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.locations,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/locations")).locations,
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: keys.suppliers,
    queryFn: async () => unwrap(await api.GET("/api/v1/suppliers")).suppliers,
  });
}

/** The sellers the catalog knows, to compare a supplier typed by hand against. Rarely changes. */
export function useCatalogSuppliers() {
  return useQuery({
    queryKey: keys.catalogSuppliers,
    queryFn: async () => unwrap(await api.GET("/api/v1/catalog/suppliers")).suppliers,
    staleTime: 10 * 60_000,
  });
}

export function useMovements(filter: { itemId?: string; locationId?: string; limit?: number }) {
  return useQuery({
    queryKey: keys.movements(filter),
    queryFn: async () => unwrap(await api.GET("/api/v1/movements", { params: { query: filter } })),
  });
}

/**
 * The movement log, newest first, a hundred entries at a time, each page older than the last: all of
 * it, or one kind, which the server picks out so every page is a hundred of that kind. A refetch walks
 * the pages again from the newest, so an entry recorded meanwhile moves none of them.
 */
export function useMovementLog(kind: MovementKind | null = null) {
  return useInfiniteQuery({
    queryKey: keys.movementLog(kind),
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/movements", {
          params: { query: { before: pageParam, limit: 100, kind: kind ?? undefined } },
        }),
      ),
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
}

/** Catalog search. The catalog is never listed, so nothing is asked for until two characters are typed. */
export function useCatalog(q: string, category: Category | null) {
  const query = q.trim();
  return useQuery({
    queryKey: keys.catalog(query, category),
    enabled: query.length >= 2,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/catalog/products", {
          params: { query: { q: query, category: category ?? undefined } },
        }),
      ).products,
  });
}

/** The whole catalog, a page at a time, only when someone chooses to view it. */
export function useCatalogBrowse(category: Category | null) {
  return useInfiniteQuery({
    queryKey: ["catalog-browse", category] as const,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET("/api/v1/catalog/products", {
          params: {
            query: { browse: "true", offset: pageParam, category: category ?? undefined },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });
}

export type Maker = Schemas["Maker"];

export function useMakers() {
  return useQuery({
    queryKey: ["makers"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/makers")).makers,
  });
}

export type PackOption = Schemas["PackOption"];
export type NewPack = Schemas["NewPack"];

/** The packs an item of this kind comes in: the brewery's own names first, then the known list. */
export function usePacks(dimension: Schemas["Dimension"], category?: Category | null) {
  return useQuery({
    queryKey: ["packs", dimension, category ?? null] as const,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/packs", {
          params: { query: { dimension, ...(category ? { category } : {}) } },
        }),
      ).packs,
    staleTime: 60_000,
  });
}

export type Packaging = Schemas["Packaging"];
export type PackagingEntry = Schemas["PackagingEntry"];

/** Settings > Packaging: what each kind of item comes in. */
export function usePackaging() {
  return useQuery({
    queryKey: ["packaging"] as const,
    queryFn: async () => unwrap(await api.GET("/api/v1/packaging")).packaging,
  });
}

/** The packs for several kinds of item at once, by `${dimension}|${category}`; shares usePacks' cache. */
export function usePackLists(
  kinds: readonly { dimension: Schemas["Dimension"]; category: Category | null }[],
): Map<string, Schemas["PackOption"][]> {
  const unique = [...new Map(kinds.map((k) => [`${k.dimension}|${k.category ?? ""}`, k])).values()];
  const results = useQueries({
    queries: unique.map(({ dimension, category }) => ({
      queryKey: ["packs", dimension, category ?? null] as const,
      queryFn: async () =>
        unwrap(
          await api.GET("/api/v1/packs", {
            params: { query: { dimension, ...(category ? { category } : {}) } },
          }),
        ).packs,
      staleTime: 60_000,
    })),
  });
  return new Map(
    unique.map((k, i) => [`${k.dimension}|${k.category ?? ""}`, results[i]?.data ?? []]),
  );
}

export function useInvoices(
  status?: "draft" | "committed" | "discarded",
  { enabled }: WhenWanted = {},
) {
  return useQuery({
    queryKey: [...keys.invoices, status ?? "all"] as const,
    enabled,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/invoices", { params: { query: { status } } })).invoices,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: keys.invoice(id),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/invoices/{id}", { params: { path: { id } } })),
  });
}

/**
 * The document, read and turned into a draft in one go. A supplier no template knows comes back as
 * `unknown_supplier` with the file already stored, so it can be typed in beside it instead.
 */
export async function parseInvoiceFile(file: File, key: string) {
  const body = new FormData();
  body.set("file", file);
  const response = await fetch("/api/v1/invoices/parse", {
    method: "POST",
    headers: { "idempotency-key": key },
    body,
  });
  const data = (await response.json()) as Invoice & {
    fileId?: string;
    error?: { code?: string; message?: string };
  };
  if (response.ok) return { invoice: data as Invoice, fileId: null, reason: null };
  if (response.status === 422 && data.error?.code === "unknown_supplier") {
    return { invoice: null, fileId: data.fileId ?? null, reason: data.error.message ?? null };
  }
  throw new ApiError(
    response.status,
    data.error?.code ?? "error",
    data.error?.message ?? `Could not read that invoice (${response.status})`,
  );
}

/** Uploads the document on its own, before a draft exists, so a duplicate is caught up front. */
export async function uploadInvoiceFile(file: File, key: string) {
  const body = new FormData();
  body.set("file", file);
  const response = await fetch("/api/v1/invoice-files", {
    method: "POST",
    headers: { "idempotency-key": key },
    body,
  });
  const data = (await response.json()) as {
    fileId?: string;
    byteSize?: number;
    existingInvoiceId?: string | null;
    error?: { code?: string; message?: string };
  };
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error?.code ?? "error",
      data.error?.message ?? `Upload failed (${response.status})`,
    );
  }
  return data as { fileId: string; byteSize: number; existingInvoiceId: string | null };
}

export function useRecipes({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.recipes,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/recipes")).recipes,
  });
}

export function useRecipe(id: string) {
  return useQuery({
    queryKey: keys.recipe(id),
    enabled: id !== "",
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/recipes/{id}", { params: { path: { id } } })),
  });
}

export function useStockImports({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.stockImports,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/stock-imports")).imports,
  });
}

export function useStockImport(id: string) {
  return useQuery({
    queryKey: keys.stockImport(id),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/stock-imports/{id}", { params: { path: { id } } })),
  });
}

/** Reads Ollie's Inventory On Hand Report for review; nothing touches stock yet. */
export async function importOllieReport(file: File, key: string): Promise<StockImport> {
  const body = new FormData();
  body.set("file", file);
  const response = await fetch("/api/v1/stock-imports", {
    method: "POST",
    headers: { "idempotency-key": key },
    body,
  });
  const data = (await response.json()) as StockImport & {
    error?: { code?: string; message?: string };
  };
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error?.code ?? "error",
      data.error?.message ?? `Could not read that report (${response.status})`,
    );
  }
  return data;
}

export function useBrewSystems({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.brewSystems,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/brew-systems")).brewSystems,
  });
}

/** The cellar's tanks, retired ones too, each with what is in it and booked into it. */
export function useTanks({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.tanks,
    enabled,
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/tanks", { params: { query: { today: today() } } })).tanks,
  });
}

/**
 * Every stay in any tank between two days, and the brews not yet brewed with no tank: what the tank
 * schedule draws. The days before stay on screen while a wider span loads.
 */
export function useTankStays(from: string, to: string) {
  return useQuery({
    queryKey: keys.tankStays(from, to),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/tank-stays", { params: { query: { from, to, today: today() } } }),
      ),
  });
}

/** The size of the brewery's Brewery layout. */
export function useCellarSettings({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.cellarSettings,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/cellar-settings")),
  });
}

export function useRecipeDrafts({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.recipeDrafts,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/recipe-drafts")).drafts,
  });
}

/** A draft by its id; one the server never got is not an error, only nothing yet. */
export function useRecipeDraft(id: string | null) {
  return useQuery({
    queryKey: keys.recipeDraft(id ?? ""),
    enabled: id !== null,
    retry: false,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/recipe-drafts/{id}", {
        params: { path: { id: id ?? "" } },
      });
      if (response.status === 404) return null;
      if (!data) {
        throw new ApiError(
          response.status,
          "unknown",
          `The draft did not load (${response.status})`,
        );
      }
      return data;
    },
  });
}

/** `refetchInterval` keeps a view live, catching brews changed on another device. */
/** A recurring brew: its rule, its version and its brews. */
export function useBrewSeries(id: string | null) {
  return useQuery({
    queryKey: keys.brewSeries(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () =>
      unwrap(await api.GET("/api/v1/brew-series/{id}", { params: { path: { id: id as string } } })),
  });
}

export function useBrews({
  refetchInterval,
  enabled,
}: { refetchInterval?: number } & WhenWanted = {}) {
  return useQuery({
    queryKey: keys.brews,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/brews")).brews,
    refetchInterval,
  });
}

export function useBrew(id: string, { refetchInterval }: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: keys.brew(id),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/brews/{id}", {
          params: { path: { id }, query: { today: today() } },
        }),
      ),
    refetchInterval,
  });
}

/** What to order to brew everything on the schedule through a date. */
export function useShortfall(through: string) {
  return useQuery({
    queryKey: keys.shortfall(through),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/schedule/shortfall", {
          params: { query: { through, today: today() } },
        }),
      ),
  });
}

/** Every recipe in a BeerXML or BeerSmith (.bsmx) file, added as written. */
export async function importRecipeFile(file: File, key: string) {
  const body = new FormData();
  body.set("file", file);
  const response = await fetch("/api/v1/recipes/import", {
    method: "POST",
    headers: { "idempotency-key": key },
    body,
  });
  const data = (await response.json()) as {
    recipes?: RecipeSummary[];
    error?: { code?: string; message?: string };
  };
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error?.code ?? "error",
      data.error?.message ?? `Could not import that file (${response.status})`,
    );
  }
  return data.recipes ?? [];
}

export function useBorrowing({ enabled }: WhenWanted = {}) {
  return useQuery({
    queryKey: keys.borrowing,
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/v1/borrowing")).balances,
  });
}

export function newKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * A change the person intends to make, sent with one idempotency key (invariant 2). The key is kept
 * until the server has answered: pressing Save again after a dropped connection resends the same key,
 * so the server applies it once. A fresh key is minted only after success or a definite rejection.
 */
export function useChange<Input, Output>(
  send: (input: Input, key: string) => Promise<Output>,
  options: { onSuccess?: (output: Output) => void } = {},
) {
  const queryClient = useQueryClient();
  const key = useRef(newKey());
  const mutation = useMutation({
    mutationFn: (input: Input) => send(input, key.current),
    onSuccess: async (output) => {
      key.current = newKey();
      await queryClient.invalidateQueries();
      options.onSuccess?.(output);
    },
    onError: (error) => {
      // A reply from the server means it did not apply; anything else might have, so keep the key.
      if (error instanceof ApiError) key.current = newKey();
    },
  });
  const reset = useCallback(() => {
    key.current = newKey();
    mutation.reset();
  }, [mutation]);
  return { ...mutation, reset };
}

/**
 * Where notifications are kept: the brewery's database, for thirty days (Tyler, 2026-09-24). A
 * notification is sent under its own id as the idempotency key, so a retry is the same notification.
 */
export function serverNoteStore(): NoteStore {
  return {
    load: async () =>
      unwrap(await api.GET("/api/v1/notifications", { params: { query: { limit: 500 } } }))
        .notifications,
    add: async (note) => {
      unwrap(
        await api.POST("/api/v1/notifications", {
          body: note,
          params: { header: { "idempotency-key": note.id } },
        }),
      );
    },
    readAll: async () => {
      unwrap(
        await api.POST("/api/v1/notifications/read", {
          params: { header: { "idempotency-key": newKey() } },
        }),
      );
    },
    actionDone: async (id) => {
      unwrap(
        await api.POST("/api/v1/notifications/{id}/done", {
          params: { path: { id }, header: { "idempotency-key": newKey() } },
        }),
      );
    },
  };
}

/**
 * Puts a received invoice back. From a notification — the bubble, the bell or the Notifications page —
 * which may be pressed long after the invoice was left behind. Put back already, from the History page
 * or another device, counts as done: what was asked for is true.
 */
export async function undoReceipt(invoiceId: string): Promise<void> {
  try {
    await send.undoInvoice(invoiceId, newKey());
  } catch (error) {
    if (!(error instanceof ApiError && error.code === "already_reversed")) throw error;
  }
}

export const send = {
  movement: async (body: MovementInput, key: string) =>
    unwrap(
      await api.POST("/api/v1/movements", {
        body,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  reverse: async (input: { id: string; note?: string }, key: string) =>
    unwrap(
      await api.POST("/api/v1/movements/{id}/reverse", {
        body: { note: input.note },
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  item: async (body: NewItem, key: string) =>
    unwrap(
      await api.POST("/api/v1/items", { body, params: { header: { "idempotency-key": key } } }),
    ),
  patchItem: async (input: { id: string; patch: ItemPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/items/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  linkItem: async (
    input: { id: string; catalogProductId: string; mergeItemId?: string },
    key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/items/{id}/catalog-link", {
        body: { catalogProductId: input.catalogProductId, mergeItemId: input.mergeItemId },
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  createRecipe: async (input: { body: RecipeInput; draft?: string }, key: string) =>
    unwrap(
      await api.POST("/api/v1/recipes", {
        body: input.body,
        params: { query: { draft: input.draft }, header: { "idempotency-key": key } },
      }),
    ),
  saveRecipe: async (input: { id: string; body: RecipeInput; draft?: string }, key: string) =>
    unwrap(
      await api.PUT("/api/v1/recipes/{id}", {
        body: input.body,
        params: {
          path: { id: input.id },
          query: { draft: input.draft },
          header: { "idempotency-key": key },
        },
      }),
    ),
  commitStockImport: async (input: { id: string; body: StockImportCommit }, key: string) =>
    unwrap(
      await api.POST("/api/v1/stock-imports/{id}/commit", {
        body: input.body,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  undoStockImport: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/stock-imports/{id}/undo", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  discardStockImport: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/stock-imports/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  favoriteRecipe: async (input: { id: string; favorite: boolean }, key: string) =>
    unwrap(
      await api.PUT("/api/v1/recipes/{id}/favorite", {
        body: { favorite: input.favorite },
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  createBrewSystem: async (body: BrewSystemInput, key: string) =>
    unwrap(
      await api.POST("/api/v1/brew-systems", {
        body,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  saveBrewSystem: async (input: { id: string; body: BrewSystemInput }, key: string) =>
    unwrap(
      await api.PUT("/api/v1/brew-systems/{id}", {
        body: input.body,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  deleteBrewSystem: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/brew-systems/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  createTank: async (body: TankInput, key: string) =>
    unwrap(
      await api.POST("/api/v1/tanks", { body, params: { header: { "idempotency-key": key } } }),
    ),
  saveTank: async (input: { id: string; patch: TankPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/tanks/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  deleteTank: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/tanks/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  addStay: async (
    input: {
      brewId: string;
      tankId: string;
      inOn?: string;
      dueOutOn?: string | null;
      filled?: boolean;
    },
    key: string,
  ) => {
    const { brewId, ...body } = input;
    return unwrap(
      await api.POST("/api/v1/brews/{id}/stays", {
        body,
        params: { path: { id: brewId }, header: { "idempotency-key": key } },
      }),
    );
  },
  changeStay: async (input: { id: string; patch: TankStayPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/tank-stays/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  moveStay: async (
    input: { id: string; tankId: string; on: string; dueOutOn?: string | null },
    key: string,
  ) => {
    const { id, ...body } = input;
    return unwrap(
      await api.POST("/api/v1/tank-stays/{id}/move", {
        body,
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    );
  },
  removeStay: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/tank-stays/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  saveCellarSettings: async (body: Partial<CellarSettings>, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/cellar-settings", {
        body,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  discardRecipeDraft: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/recipe-drafts/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  newRecipeVersion: async (input: { id: string; body: RecipeInput; draft?: string }, key: string) =>
    unwrap(
      await api.POST("/api/v1/recipes/{id}/versions", {
        body: input.body,
        params: {
          path: { id: input.id },
          query: { draft: input.draft },
          header: { "idempotency-key": key },
        },
      }),
    ),
  startBrew: async (recipeId: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/recipes/{id}/brews", {
        params: { path: { id: recipeId }, header: { "idempotency-key": key } },
      }),
    ),
  planBrew: async (
    input: { recipeId: string; plannedOn: string; keepVersion?: boolean; tankId?: string },
    key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/recipes/{id}/brews", {
        body: { plannedOn: input.plannedOn, keepVersion: input.keepVersion, tankId: input.tankId },
        params: { path: { id: input.recipeId }, header: { "idempotency-key": key } },
      }),
    ),
  /** A recurring brew: on the schedule for every day at once, all or none. */
  planBrews: async (
    input: {
      recipeId: string;
      days: string[];
      keepVersion?: boolean;
      repeat?: RepeatRule;
      until?: string;
    },
    key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/recipes/{id}/brews/planned", {
        body: {
          days: input.days,
          keepVersion: input.keepVersion,
          repeat: input.repeat,
          until: input.until,
        },
        params: { path: { id: input.recipeId }, header: { "idempotency-key": key } },
      }),
    ).brews,
  startBrewDay: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/brews/{id}/start", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  saveBrew: async (input: { id: string; patch: BrewPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/brews/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  brewed: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/brews/{id}/brewed", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  assignBrewPull: async (
    input: { id: string; pullId: string; lotId: string; locationId: string; quantity: string },
    key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/brews/{id}/assign", {
        body: {
          pullId: input.pullId,
          lotId: input.lotId,
          locationId: input.locationId,
          quantity: input.quantity,
        },
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  harvest: async (input: HarvestInput, key: string) =>
    unwrap(
      await api.POST("/api/v1/harvests", {
        body: input,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  undoBrew: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/brews/{id}/undo", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  editSeries: async (input: { id: string; patch: BrewSeriesPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/brew-series/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  deleteSeries: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/brew-series/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  deleteBrew: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/brews/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  linkRecipeLine: async (
    input: { recipeId: string; lineId: string; itemId: string | null },
    key: string,
  ) =>
    unwrap(
      await api.PATCH("/api/v1/recipes/{id}/lines/{lineId}", {
        body: { itemId: input.itemId },
        params: {
          path: { id: input.recipeId, lineId: input.lineId },
          header: { "idempotency-key": key },
        },
      }),
    ),
  archiveRecipe: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/recipes/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  deleteItem: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/items/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  patchLot: async (input: { id: string; patch: LotPatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/lots/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  location: async (body: { name: string; sortOrder?: number }, key: string) =>
    unwrap(
      await api.POST("/api/v1/locations", { body, params: { header: { "idempotency-key": key } } }),
    ),
  patchLocation: async (
    input: {
      id: string;
      patch: { name?: string; sortOrder?: number; archived?: boolean; defaultFor?: LocationKind[] };
    },
    key: string,
  ) =>
    unwrap(
      await api.PATCH("/api/v1/locations/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  maker: async (body: { maker: string; shortName: string }, key: string) =>
    unwrap(
      await api.POST("/api/v1/makers", { body, params: { header: { "idempotency-key": key } } }),
    ),
  resetMakers: async (key: string) =>
    unwrap(
      await api.POST("/api/v1/makers/reset", { params: { header: { "idempotency-key": key } } }),
    ),
  // No idempotency key: the reset empties the table those keys live in. See routes/admin.ts.
  resetTenant: async (parts?: ResetPart[]) =>
    unwrap(await api.POST("/api/v1/tenant/reset", parts ? { body: { parts } } : {})),
  invoice: async (body: Schemas["NewInvoice"], key: string) =>
    unwrap(
      await api.POST("/api/v1/invoices", { body, params: { header: { "idempotency-key": key } } }),
    ),
  patchInvoice: async (input: { id: string; patch: InvoicePatch }, key: string) =>
    unwrap(
      await api.PATCH("/api/v1/invoices/{id}", {
        body: input.patch,
        params: { path: { id: input.id }, header: { "idempotency-key": key } },
      }),
    ),
  commitInvoice: async (
    input: { id: string; locationId: string; commitKey: string },
    _key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/invoices/{id}/commit", {
        body: { locationId: input.locationId },
        // The draft's own key, minted when it was created, so a retry can never receive it twice.
        params: { path: { id: input.id }, header: { "idempotency-key": input.commitKey } },
      }),
    ),
  deleteInvoice: async (id: string, key: string) =>
    unwrap(
      await api.DELETE("/api/v1/invoices/{id}", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  undoInvoice: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/invoices/{id}/undo", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  discardInvoice: async (id: string, key: string) =>
    unwrap(
      await api.POST("/api/v1/invoices/{id}/discard", {
        params: { path: { id }, header: { "idempotency-key": key } },
      }),
    ),
  /** Sync on the lots pass: the lot number off the bag, looked up with the supplier, figures on the lot. */
  syncCertificate: async (
    input: { invoiceId: string; lineId: string; body: Schemas["CertificateSync"] },
    key: string,
  ) =>
    unwrap(
      await api.POST("/api/v1/invoices/{id}/lines/{lineId}/certificate", {
        body: input.body,
        params: {
          path: { id: input.invoiceId, lineId: input.lineId },
          header: { "idempotency-key": key },
        },
      }),
    ),
  namePack: async (body: NewPack, key: string) =>
    unwrap(
      await api.POST("/api/v1/packs", { body, params: { header: { "idempotency-key": key } } }),
    ),
  setPackaging: async (body: Schemas["PackagingSet"], key: string) =>
    unwrap(
      await api.POST("/api/v1/packaging", { body, params: { header: { "idempotency-key": key } } }),
    ),
  removePackaging: async (body: Schemas["PackagingSize"], key: string) =>
    unwrap(
      await api.POST("/api/v1/packaging/remove", {
        body,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  restorePackaging: async (body: Schemas["PackagingSize"], key: string) =>
    unwrap(
      await api.POST("/api/v1/packaging/restore", {
        body,
        params: { header: { "idempotency-key": key } },
      }),
    ),
  supplier: async (body: { name: string }, key: string) =>
    unwrap(
      await api.POST("/api/v1/suppliers", { body, params: { header: { "idempotency-key": key } } }),
    ),
};
